import {
  addNewEntry,
  deleteEntry,
  parseSrtFile,
  splitEntry,
  squashUpEntry,
  stringifySrtNodes,
  updateEntryText,
  updateEntryTime,
} from "./srt.ts";
import { type SrtNode, stringifyMsToTime } from "@leawind/srt-parser/internal";

import { render } from "preact";
import { useCallback, useRef, useState } from "preact/hooks";
import { computed, signal, useSignalEffect } from "@preact/signals";
import type { RefObject } from "preact";

interface SubtitleEntryProps {
  entry: SrtNode;
  onTimeChange: (
    id: number,
    field: "appear" | "disappear",
    value: string,
  ) => void;
  onTextChange: (id: number, value: string) => void;
  onSplit: (id: number) => void;
  onSquashUp: (id: number) => void;
  onDelete: (id: number) => void;
  isFirst: boolean;
  isFocused: boolean;
  onFocus: (id: number) => void;
}

function SubtitleEntry(
  {
    entry,
    onTimeChange,
    onTextChange,
    onSplit,
    onSquashUp,
    onDelete,
    isFirst,
    isFocused,
    onFocus,
  }: SubtitleEntryProps,
) {
  return (
    <div
      class={`entry ${isFocused ? "focused" : ""}`}
      onClick={() => onFocus(entry.id)}
    >
      <div class="entry-header">
        <span class="entry-id">#{entry.id}</span>
        <div class="timecodes">
          <div>{stringifyMsToTime(entry.disappear - entry.appear)}</div>
          <div class="timecode-field">
            <input
              type="text"
              value={stringifyMsToTime(entry.appear)}
              onChange={(e) =>
                onTimeChange(
                  entry.id,
                  "appear",
                  (e.target as HTMLInputElement).value,
                )}
              placeholder="00:00:00,000"
            />
          </div>
          <div class="timecode-separator">→</div>
          <div class="timecode-field">
            <input
              type="text"
              value={stringifyMsToTime(entry.disappear)}
              onChange={(e) =>
                onTimeChange(
                  entry.id,
                  "disappear",
                  (e.target as HTMLInputElement).value,
                )}
              placeholder="00:00:00,000"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => onSplit(entry.id)}
          class="split-button"
          title="2行以上のテキストを行で分割。時間も文字数に応じて分割。"
          disabled={!entry.subtitle.includes("\n")}
        >
          ✂️
        </button>
        {!isFirst && (
          <button
            type="button"
            onClick={() => onSquashUp(entry.id)}
            class="squash-button"
            title="テキストと時間を前のエントリと結合"
          >
            ⬆️
          </button>
        )}
        <button
          type="button"
          onClick={() => onDelete(entry.id)}
          class="delete-button"
          title="Delete entry"
        >
          🗑️
        </button>
      </div>
      <div class="text-field">
        <textarea
          value={entry.subtitle}
          onChange={(e) =>
            onTextChange(entry.id, (e.target as HTMLTextAreaElement).value)}
          rows={3}
          placeholder="Subtitle text..."
        />
      </div>
    </div>
  );
}

interface MediaPlayerProps {
  entries: SrtNode[];
  setFocusedEntryId: (id: number | null) => void;
}

const currentTime = signal(0);
const currentTimeDisp = signal(0);
function MediaPlayer({
  entries,
  setFocusedEntryId,
}: MediaPlayerProps) {
  const [mediaFile, setMediaFile] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRef = useRef<HTMLVideoElement | HTMLAudioElement>(null);

  useSignalEffect(() => {
    const time = currentTime.value;
    const media = mediaRef.current;
    if (media) media.currentTime = time;
  });

  const handleMediaUpload = useCallback((e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    setMediaFile(url);
    setIsPlaying(false);
    currentTimeDisp.value = 0;
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const media = mediaRef.current;
    if (media) {
      currentTimeDisp.value = media.currentTime;

      // 現在の時刻に対応するエントリを見つけてフォーカス
      const currentMs = media.currentTime * 1000;
      const currentEntry = entries.find(
        (entry) => entry.appear <= currentMs && currentMs <= entry.disappear,
      );
      if (currentEntry) setFocusedEntryId(currentEntry.id);
    }
  }, [entries, setFocusedEntryId]);

  const handleLoadedMetadata = useCallback(() => {
    const media = mediaRef.current;
    if (media) {
      setDuration(media.duration);
    }
  }, []);

  const handleProgressBarClick = useCallback((e: MouseEvent) => {
    const media = mediaRef.current;
    if (!media || !duration) return;

    const progressBar = e.currentTarget as HTMLElement;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;

    media.currentTime = newTime;
    currentTimeDisp.value = newTime;
  }, [duration, currentTimeDisp]);

  const handlePlayPause = useCallback(() => {
    const media = mediaRef.current;
    if (!media) return;

    if (isPlaying) {
      media.pause();
    } else {
      media.play();
    }
  }, [isPlaying, entries]);

  const formatTime = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${
      s.toString().padStart(2, "0")
    }`;
  };

  const timeDisplay = computed(() =>
    `${formatTime(currentTimeDisp.value)} / ${formatTime(duration)}`
  );
  return (
    <>
      <div class="media-player">
        {!mediaFile
          ? (
            <>
              <label for="media-upload" class="media-upload-button">
                🎬 Open Media
              </label>
              <input
                id="media-upload"
                type="file"
                accept="video/*,audio/*"
                onChange={handleMediaUpload}
                style={{ display: "none" }}
              />
            </>
          )
          : (
            <div class="media-player-controls">
              <button
                type="button"
                onClick={handlePlayPause}
                class="play-button"
              >
                {isPlaying ? "⏸" : "▶"}
              </button>
              <span class="time-display">
                {timeDisplay}
              </span>
              <div class="progress-bar" onClick={handleProgressBarClick}>
                <div
                  class="progress-fill"
                  style={{
                    width: `${(currentTimeDisp.value / duration) * 100}%`,
                  }}
                />
              </div>
            </div>
          )}
      </div>

      {mediaFile && (
        <video
          ref={mediaRef as RefObject<HTMLVideoElement>}
          src={mediaFile}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={() => {
            setIsPlaying(true);
          }}
          onPause={() => {
            setIsPlaying(false);
          }}
          onEnded={() => {
            setIsPlaying(false);
          }}
          style={{ display: "none" }}
        />
      )}
    </>
  );
}

function App() {
  const [entries, setEntries] = useState<SrtNode[]>([]);
  const [fileName, setFileName] = useState("");
  const [focusedEntryId, setFocusedEntryId] = useState<number | null>(null);

  const handleFileUpload = useCallback((e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      try {
        const nodes = parseSrtFile(content);
        setEntries(nodes);
      } catch (error) {
        console.error("Failed to parse SRT:", error);
        alert("Failed to parse SRT file. Please check the file format.");
      }
    };
    reader.readAsText(file);
  }, []);

  const handleTimeChange = useCallback(
    (id: number, field: "appear" | "disappear", value: string) => {
      setEntries((prev) => updateEntryTime(prev, id, field, value));
    },
    [],
  );

  const handleTextChange = useCallback((id: number, value: string) => {
    setEntries((prev) => updateEntryText(prev, id, value));
  }, []);

  const handleDownload = useCallback(() => {
    const content = stringifySrtNodes(entries);
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "subtitles.srt";
    a.click();
    URL.revokeObjectURL(url);
  }, [entries, fileName]);

  const handleAddEntry = useCallback(() => {
    setEntries((prev) => addNewEntry(prev));
  }, []);

  const handleDeleteEntry = useCallback((id: number) => {
    setEntries((prev) => deleteEntry(prev, id));
  }, []);

  const handleSplitEntry = useCallback((id: number) => {
    setEntries((prev) => splitEntry(prev, id));
  }, []);

  const handleSquashUpEntry = useCallback((id: number) => {
    setEntries((prev) => squashUpEntry(prev, id));
  }, []);

  const handleEntryFocus = useCallback((id: number) => {
    setFocusedEntryId(id);
    const entry = entries.find((e) => e.id === id);
    if (entry) {
      currentTime.value = entry.appear / 1000;
    }
  }, [entries]);

  return (
    <div class="container">
      <header>
        <h1>SRT Editor</h1>
        <MediaPlayer
          entries={entries}
          setFocusedEntryId={setFocusedEntryId}
        />
      </header>

      <div class="controls">
        <div class="upload-section">
          <label for="file-upload" class="button">
            📁 Open SRT File
          </label>
          <input
            id="file-upload"
            type="file"
            accept=".srt"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
          {fileName && <span class="file-name">{fileName}</span>}
        </div>

        <div class="action-buttons">
          {entries.length > 0 && (
            <button
              type="button"
              onClick={handleDownload}
              class="button primary"
            >
              💾 Download SRT
            </button>
          )}
        </div>
      </div>

      <div class="editor">
        {entries.length === 0
          ? (
            <div class="empty-state">
              <p>No subtitles loaded. Upload an SRT file to get started.</p>
            </div>
          )
          : (
            <div class="entries">
              {entries.map((entry, index) => (
                <SubtitleEntry
                  key={entry.id}
                  entry={entry}
                  onTimeChange={handleTimeChange}
                  onTextChange={handleTextChange}
                  onSplit={handleSplitEntry}
                  onSquashUp={handleSquashUpEntry}
                  onDelete={handleDeleteEntry}
                  isFirst={index === 0}
                  isFocused={entry.id === focusedEntryId}
                  onFocus={handleEntryFocus}
                />
              ))}
            </div>
          )}
      </div>
      <button type="button" onClick={handleAddEntry} class="button">
        ➕ Add Entry
      </button>
    </div>
  );
}

if (typeof document !== "undefined") {
  render(<App />, document.getElementById("app")!);
}

export default App;
